import type { SubscriptionStatus } from '../context/SubscriptionContext';

export const WARN_DAYS = 14;

export type ExpirySeverity = 'warning' | 'expired';

export type ExpiryNotice = {
  severity: ExpirySeverity;
  title: string;
  message: string;
};

function daysUntil(iso: string): number {
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE');
}

const PLAN_LABELS: Record<string, string> = {
  pro: 'Pro', unlimited: 'Unlimited',
  starter: 'Starter', professional: 'Professional', business: 'Business', flat: 'Flat',
  partner: 'Partner',
};

export function buildExpiryNotices(status: SubscriptionStatus | null): ExpiryNotice[] {
  if (!status) return [];
  const notices: ExpiryNotice[] = [];
  const { effectivePlan, rawPlan, planExpiresAt, addons } = status;
  const hadPaidPlan = rawPlan !== 'free';

  if (planExpiresAt && hadPaidPlan) {
    const days = daysUntil(planExpiresAt);
    if (effectivePlan !== 'free' && days >= 0 && days <= WARN_DAYS) {
      const planLabel = PLAN_LABELS[effectivePlan] ?? effectivePlan;
      notices.push({
        severity: 'warning',
        title: `${planLabel}-Plan läuft bald ab`,
        message:
          days === 0
            ? 'Dein Plan läuft heute ab. Verlängere jetzt, damit alle Funktionen erhalten bleiben.'
            : `Dein Plan läuft in ${days} ${days === 1 ? 'Tag' : 'Tagen'} (${formatDate(planExpiresAt)}) ab.`,
      });
    } else if (effectivePlan === 'free' && days < 0) {
      notices.push({
        severity: 'expired',
        title: 'Plan abgelaufen',
        message: `Dein bezahlter Plan ist am ${formatDate(planExpiresAt)} abgelaufen. Du nutzt aktuell den Free-Plan.`,
      });
    }
  }

  if (addons.startklarExpiresAt) {
    const days = daysUntil(addons.startklarExpiresAt);
    if (addons.startklar && days >= 0 && days <= WARN_DAYS) {
      notices.push({
        severity: 'warning',
        title: 'Startklar-Programm endet bald',
        message:
          days === 0
            ? 'Dein Startklar-Programm endet heute. Buche danach den Pro-Plan (monatlich oder jährlich), um nahtlos weiterzuarbeiten.'
            : `Dein Startklar-Programm endet in ${days} ${days === 1 ? 'Tag' : 'Tagen'} (${formatDate(addons.startklarExpiresAt)}). Buche danach den Pro-Plan (monatlich oder jährlich), um nahtlos weiterzuarbeiten.`,
      });
    } else if (!addons.startklar && days < 0) {
      notices.push({
        severity: 'expired',
        title: 'Startklar-Programm beendet',
        message: `Dein Startklar-Programm endete am ${formatDate(addons.startklarExpiresAt)}. Buche jetzt den Pro-Plan, um weiterzuarbeiten.`,
      });
    }
  }

  return notices;
}
